import React, { Component, Fragment } from 'react';
import {connect} from "react-redux";
import {IRootState} from "../../../../store";
import DispatchProps from "../../../../utils/DispatchProps";
import PageTitle from "../../../PageTitle";
import ApiService from "../../../ApiService";
import H from "history";
import TenantSummary from "../../../../models/tenants/TenantSummary";
import {UserAccessLevel} from "../../../../models/UserAccessRecord";
import {MessageBar, MessageBarType, PrimaryButton, TextField} from "office-ui-fabric-react";
import {TotpStatus} from "../../../../models/tenants/TotpStatus";
import LoadingPage from "../../../LoadingPage";
import moment from "moment";
import {FRIENDLY_DATETIME_FORMAT} from "../../../../utils/Utils";
import TotpCode from "../../../tenants/TotpCode";
import Actions from "../../../../redux/Actions";

interface SupportMfaPageProps {
    tenantId: string;

    apiService: ApiService;
    history: H.History;
    usersCdbAccessLevel?: UserAccessLevel;
}

interface SupportMfaPageState {
    tenant?: TenantSummary | null;
    totpStatus?: TotpStatus;
    
    savingSecret: boolean;
    newSecret: string;
    
    loadingCode: boolean;
    code?: string;
}

class SupportMfaPage extends Component<SupportMfaPageProps & DispatchProps ,SupportMfaPageState> {
    static displayName = SupportMfaPage.name;

    constructor(props: SupportMfaPageProps & DispatchProps) {
        super(props);

        this.state = {
            tenant: undefined,
            
            savingSecret: false,
            newSecret: "",
            
            loadingCode: false,
            code: undefined
        }
    }

    async componentDidMount() {

        const tenant = await this.props.apiService.getTenantAdmin(this.props.tenantId);
        this.setState({
            tenant
        });
        
        await this.getStatus();
    }
    
    private getStatus = async () => {
        const totpStatus = await this.props.apiService.getTotpStatusForTenant(this.props.tenantId);
        this.setState({
            totpStatus
        })
    }
    
    async componentDidUpdate(prevProps: Readonly<SupportMfaPageProps & DispatchProps>, prevState: SupportMfaPageState): Promise<void> {
    
    }

    private getCode = async () =>
    {
        if(!this.state.totpStatus?.exists)
        {
            return;
        }

        try
        {
            this.setState({ loadingCode: true});

            const newCode = await this.props.apiService.getTotpTokenForTenant(this.props.tenantId);
            this.props.dispatch(Actions.showMessageBar(`Successfully got MFA code`, MessageBarType.success));

            this.setState({
                code: newCode,
                loadingCode: false
            });

            await this.getStatus();
        }
        catch (e)
        {
            this.props.dispatch(Actions.showMessageBar(`Error getting code : ${e}`, MessageBarType.error));
            this.setState({
                loadingCode: false
            })
        }
        
    }
    
    private renderTokenCard(): JSX.Element
    {
        const noSecretMessageBar = (
            <MessageBar messageBarType={MessageBarType.warning}>
                A secret must be added before codes can be generated
            </MessageBar>
        );
        
        let lastStatus: JSX.Element | null = null;
        if(this.state.totpStatus?.lastGetTokenById)
        {
            lastStatus = <span className={"p-top-05rem"}>Last code generated by {this.state.totpStatus.lastGetTokenByUpn} at {moment(this.state.totpStatus.lastGetTokenAt).format(FRIENDLY_DATETIME_FORMAT)}</span>
        }
        
        return (
            <div className={"card"}>
                <div className={"card__header"}>
                    <span className={"card__title"}>Support MFA Code at {this.state.tenant?.tenantName}</span>
                </div>
                {this.state.totpStatus?.exists ? null :  noSecretMessageBar}
                <div className={"card__content flex-column"}>
                    <div className={"flex-row m-bottom-05rem"}>
                        <TotpCode code={this.state.code}/>
                    </div>
                    <div className={"flex-row flex--align-items-flex-end"}>
                        <PrimaryButton
                            text="Get Code"
                            disabled={!this.state.totpStatus?.exists ||this.state.loadingCode}
                            onClick={this.getCode}
                        />
                    </div>
                    {lastStatus}
                </div>
            </div>
        );
    }
    
    private saveSecret = async () =>
    {
        if(!this.state.newSecret)
        {
            return;
        }
        
        try
        {
            this.setState({ savingSecret: true});
            
            await this.props.apiService.putTotpSecretForTenant(this.props.tenantId,this.state.newSecret);
            this.props.dispatch(Actions.showMessageBar(`Successfully saved secret`, MessageBarType.success));
            
            this.setState({
                newSecret: "",
                code: undefined
            });

            this.setState({
                savingSecret: false
            });
            
            await this.getStatus();
        }
        catch (e)
        {
            this.props.dispatch(Actions.showMessageBar(`Error saving secret : ${e}`, MessageBarType.error));
            this.setState({
                savingSecret: false
            });
        }
    }
    
    private renderSecretCard(): JSX.Element
    {
        let lastStatus: JSX.Element | null = null;
        if(this.state.totpStatus?.lastPutSecretById)
        {
            lastStatus = <span className={"m-top-05rem"}>Secret last saved by {this.state.totpStatus.lastPutSecretByUpn} at {moment(this.state.totpStatus.lastPutSecretAt).format(FRIENDLY_DATETIME_FORMAT)}</span>
        }
        
        return (
            <div className={"card"}>
                <div className={"card__header"}>
                    <span className={"card__title"}>Support MFA Secret at {this.state.tenant?.tenantName}</span>
                </div>
                <div className={"card__content flex-column"}>
                    <div className={"flex-row flex--align-items-flex-end"}>
                        <TextField className="card__input"
                                   label="Secret"
                                   type={"password"}
                                   disabled={this.state.savingSecret}
                                   onChange={(e,v: string | undefined) => {
                                       this.setState({
                                           newSecret: v!
                                       });
                                   }}
                                   value={this.state.newSecret}/>
    
                        <PrimaryButton
                            className={"m-left-05rem"}
                            text="Save secret"
                            disabled={this.state.savingSecret}
                            onClick={this.saveSecret}
                        />
                    </div>
                    {lastStatus}
                </div>
            </div>
        );
    }
    
    render () {
        
        if(this.state.tenant === undefined || this.state.totpStatus === undefined)
        {
            return <LoadingPage/>;
        }

        return (
            <Fragment>
                <PageTitle title={this.state.tenant?.tenantName}/>
                
                <div className={"cdb-admin-content cdb-admin-content--column"}>

                    {this.renderTokenCard()}
                    {this.renderSecretCard()}
                </div>
            </Fragment>
        );
    }
}

export default connect(
    (state: IRootState,ownProps: {match: any, history: H.History}): SupportMfaPageProps => {
        return {
            tenantId: ownProps.match.params.tenantId,
            history: ownProps.history,
            apiService: state.auth.apiService,
            usersCdbAccessLevel: state.auth.usersCdbAccessLevel
        };
    }
)(SupportMfaPage)